import { Stack, Typography } from '@mui/material';
import { useAsyncFn } from 'react-use';

import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { useReportError } from '@/hooks/useReportError';
import { useDocumentChatContext } from '@/modules/chat/contexts/DocumentChatProvider';
import { COLORS } from '@/styles/tokens/colors';

export interface DocumentChatSuggestionProps {
  question: string;
}

export function DocumentChatSuggestion({
  question,
}: DocumentChatSuggestionProps) {
  const { askQuestion } = useDocumentChatContext();
  const { reportError } = useReportError();

  const [{ loading }, handleClick] = useAsyncFn(async () => {
    try {
      await askQuestion(question);
    } catch (err) {
      reportError('Failed to ask question', err as Error);
    }
  }, [question, askQuestion, reportError]);

  return (
    <Stack
      direction="row"
      onClick={loading ? undefined : handleClick}
      sx={{
        py: 1.5,
        px: 2,
        gap: 1.5,
        borderRadius: '16px',
        border: `1px solid ${COLORS.NAVY[100]}`,
        backgroundColor: COLORS.NAVY[50],
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.6 : 1,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: COLORS.NAVY[500],
        },
      }}
    >
      <Stars01Icon size={16} sx={{ color: COLORS.TEAL[600] }} />
      <Typography
        variant="body-xs"
        sx={{
          fontWeight: 500,
          color: COLORS.TEXT.PRIMARY,
        }}
      >
        {question}
      </Typography>
    </Stack>
  );
}
